import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import marketplaceService, {
    CheckoutResponse,
    extractErrorMessage,
    MarketplaceListing,
    PaymentConfig,
} from '../../../services/marketplaceService';
import tokenService from '../../../services/tokenService';
import usePayPalSdk from '../../../hooks/usePayPalSdk';
import {
    allowedLicenseTypes,
    BuyerLicenseType,
    firstAllowedLicenseType,
    formatEuro,
    originKey,
    requiresPayPal,
    resolveSettlement,
    totalPriceCents,
} from '../../../pages/marketplaceVisibility';
import MaterialSymbol from '../../common/MaterialSymbol';
import { VisibilityBadge } from '../Seller/SellerBadges';
import PriceBlock from '../Buyer/PriceBlock';
import PayPalButtons from './PayPalButtons';

export interface CheckoutDialogProps {
    open: boolean;
    listing: MarketplaceListing | null;
    onClose: () => void;
    /** Nach abgeschlossenem Kauf (Lizenz aktiv) — z. B. Liste neu laden */
    onCompleted?: () => void;
    /** Vorgewählte Lizenzart (z. B. aus der Kaufbox der Detailseite); gilt nur, wenn sie erlaubt ist */
    initialType?: BuyerLicenseType;
}

type Step = 'configure' | 'pay' | 'done';

const TYPE_KEYS: Record<BuyerLicenseType, string> = {
    reference: 'reference',
    copy: 'copy',
    seat_copy: 'seat_copy',
};

/**
 * Checkout-Dialog des Marktplatzes (Plan 3.4/3.5). Er zeigt Herkunft, Preis-
 * Herleitung und ob eine PayPal-Zahlung folgt. Drei Pfade:
 *
 *  - `paypal`   (> 0 €, Herkunft öffentlich bzw. Gruppe mit gleichem Preis):
 *               „Weiter zur Zahlung“ legt die Order an (`POST /marketplace/checkout`),
 *               danach erscheinen die PayPal-Buttons des JS-SDK (`merchant-id` =
 *               Verkäufer aus `payment-config`); Capture über `…/orders/{id}/capture`.
 *  - `free`     (0 €): sofortige Lizenz, kein SDK, keine Rechnung.
 *  - `internal` (Gruppen-Verrechnungspreis): sofortige Lizenz mit Belastungs-
 *               anzeige, nie PayPal, kein SDK.
 *
 * Das SDK wird nur geladen, wenn die Abwicklung `paypal` ist.
 */
const CheckoutDialog: React.FC<CheckoutDialogProps> = ({ open, listing, onClose, onCompleted, initialType }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const navigate = useNavigate();

    const [type, setType] = useState<BuyerLicenseType>('reference');
    const [seats, setSeats] = useState(10);
    const [catalogs, setCatalogs] = useState<{ id: number; name: string }[]>([]);
    const [catalogId, setCatalogId] = useState<number | ''>('');
    const [step, setStep] = useState<Step>('configure');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [order, setOrder] = useState<CheckoutResponse | null>(null);
    const [config, setConfig] = useState<PaymentConfig | null>(null);
    const [configError, setConfigError] = useState(false);

    // Beim Öffnen: vorgewählte Lizenzart ist immer eine erlaubte (#525)
    useEffect(() => {
        if (!open || !listing) return;
        setType(initialType && allowedLicenseTypes(listing).includes(initialType) ? initialType : firstAllowedLicenseType(listing));
        setSeats(10);
        setCatalogId('');
        setStep('configure');
        setBusy(false);
        setError(null);
        setOrder(null);
        setConfig(null);
        setConfigError(false);
        // Nur bei Öffnen/anderem Angebot zurücksetzen — nicht, wenn nach dem Kauf
        // dieselbe Angebots-ID mit aktualisierten Daten nachgeladen wird.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, listing?.id]);

    // Zielkatalog (#509): aktive eigene Kataloge, nur bei Kopie/Teilnehmer-Lizenz relevant
    useEffect(() => {
        if (!open || !listing) return;
        let cancelled = false;
        marketplaceService
            .getOwnCatalogs(tokenService.getCompanyId())
            .then((all) => {
                if (cancelled) return;
                setCatalogs(
                    all
                        .filter((c) => c.isActive && c.name !== 'Lizenzierte Kurse')
                        .sort((a, b) => a.name.localeCompare(b.name)),
                );
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, listing?.id]);

    const settlement = listing ? resolveSettlement(listing, type, seats) : 'free';
    const payPal = requiresPayPal(settlement);

    // Konfiguration fürs SDK nur bei Abwicklung PayPal holen
    useEffect(() => {
        if (!open || !listing || !payPal) return;
        let cancelled = false;
        marketplaceService
            .getPaymentConfig(listing.id)
            .then((c) => {
                if (!cancelled) {
                    setConfig(c);
                    setConfigError(false);
                }
            })
            .catch(() => {
                if (!cancelled) setConfigError(true);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, listing?.id, payPal]);

    const sdkUsable = payPal && !!config && config.configured && config.sellerCanReceivePayments !== false;
    const sdk = usePayPalSdk({
        clientId: config?.clientId,
        merchantId: config?.sellerMerchantId,
        currency: config?.currency,
        partnerAttributionId: config?.partnerAttributionId,
        enabled: open && sdkUsable,
    });

    const allowed = useMemo(() => (listing ? allowedLicenseTypes(listing) : []), [listing]);

    const finish = useCallback(() => {
        setStep('done');
        onCompleted?.();
    }, [onCompleted]);

    const startCheckout = async () => {
        if (!listing) return;
        setBusy(true);
        setError(null);
        try {
            const result = await marketplaceService.checkout({
                listingId: listing.id,
                licenseType: type,
                ...(type === 'seat_copy' ? { seats } : {}),
                ...(type !== 'reference' && catalogId !== '' ? { catalogId: catalogId as number } : {}),
            });
            setOrder(result);
            if (result.status === 'completed') {
                notify(translate('marketplaceBuyer.checkout.success'), { type: 'success' });
                finish();
            } else {
                setStep('pay');
            }
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceBuyer.checkout.error')));
        } finally {
            setBusy(false);
        }
    };

    const capture = async (paypalOrderId: string) => {
        if (!order) return;
        setBusy(true);
        setError(null);
        try {
            await marketplaceService.captureOrder(order.orderId, paypalOrderId);
            notify(translate('marketplaceBuyer.checkout.success'), { type: 'success' });
            finish();
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceBuyer.checkout.captureError')));
        } finally {
            setBusy(false);
        }
    };

    if (!listing) return null;

    const buttonLabel = busy
        ? translate('marketplaceBuyer.checkout.processing')
        : payPal
            ? translate('marketplaceBuyer.checkout.toPayment')
            : settlement === 'internal'
                ? translate('marketplaceBuyer.checkout.getInternal')
                : translate('marketplaceBuyer.checkout.getFree');

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="checkout-dialog-title">
            <DialogTitle id="checkout-dialog-title">{translate('marketplaceBuyer.checkout.title')}</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                        <Typography variant="h6" gutterBottom>{listing.title}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <VisibilityBadge visibility={originKey(listing)} />
                        </Box>
                        <Typography variant="body2" sx={{ color: 'var(--oa-text-2)', mt: 1 }} data-testid="origin-note">
                            {translate(`marketplaceBuyer.checkout.origin.${originKey(listing)}`)}
                        </Typography>
                    </Box>

                    {listing.isBundle && !!listing.courses?.length && (
                        <Box data-testid="checkout-bundle-courses">
                            <Typography variant="subtitle2" gutterBottom>
                                {translate('marketplaceBuyer.bundle.includes', { count: listing.courses.length })}
                            </Typography>
                            <ol className="mk-ocourses" style={{ paddingLeft: 0 }}>
                                {listing.courses.map((c, i) => (
                                    <li key={c.id}>
                                        <span className="n">{i + 1}.</span>
                                        <span className="t" style={{ whiteSpace: 'normal' }}>{c.title}</span>
                                    </li>
                                ))}
                            </ol>
                            <Typography variant="caption" sx={{ color: 'var(--oa-text-2)' }}>
                                {translate('marketplaceBuyer.bundle.checkoutHint')}
                            </Typography>
                        </Box>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}

                    {step === 'configure' && (
                        <>
                            <TextField
                                select
                                fullWidth
                                label={translate('marketplaceBuyer.checkout.licenseType')}
                                value={type}
                                onChange={(e) => setType(e.target.value as BuyerLicenseType)}
                            >
                                {allowed.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        <Box>
                                            <Typography>{translate(`marketplaceBuyer.licenseType.${TYPE_KEYS[t]}.label`)}</Typography>
                                            <Typography variant="caption" sx={{ color: 'var(--oa-text-2)' }}>
                                                {translate(`marketplaceBuyer.licenseType.${TYPE_KEYS[t]}.hint`)}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </TextField>

                            {type === 'seat_copy' && (
                                <Box>
                                    <TextField
                                        label={translate('marketplaceBuyer.checkout.seats')}
                                        type="number"
                                        size="small"
                                        value={seats}
                                        onChange={(e) => setSeats(Math.max(1, parseInt(e.target.value, 10) || 1))}
                                        inputProps={{ min: 1 }}
                                        sx={{ width: 220 }}
                                    />
                                    <Typography variant="caption" display="block" sx={{ color: 'var(--oa-text-2)', mt: 1 }}>
                                        {translate('marketplaceBuyer.checkout.seatsHint')}
                                    </Typography>
                                </Box>
                            )}

                            {type !== 'reference' && (
                                <TextField
                                    select
                                    fullWidth
                                    label={translate('marketplaceBuyer.checkout.targetCatalog')}
                                    value={catalogId}
                                    onChange={(e) => setCatalogId(e.target.value === '' ? '' : Number(e.target.value))}
                                    helperText={translate('marketplaceBuyer.checkout.targetCatalogHelp')}
                                    SelectProps={{ displayEmpty: true }}
                                    InputLabelProps={{ shrink: true }}
                                >
                                    <MenuItem value="">{translate('marketplaceBuyer.checkout.defaultCatalog')}</MenuItem>
                                    {catalogs.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                    ))}
                                </TextField>
                            )}

                            <Box sx={{ p: 2, bgcolor: 'var(--oa-paper)', border: '1px solid var(--oa-divider)', borderRadius: 'var(--oa-radius-md)' }} data-testid="price-box">
                                <PriceBlock listing={listing} type={type} seats={seats} variant="large" />
                                {type === 'seat_copy' && (
                                    <Typography variant="caption" sx={{ color: 'var(--oa-text-2)' }}>
                                        {translate('marketplaceBuyer.checkout.seatsTimes', {
                                            seats,
                                            price: formatEuro(totalPriceCents(listing, type, 1) / 100, listing.currency),
                                        })}
                                    </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1.5 }} data-testid={`settlement-${settlement}`}>
                                    <MaterialSymbol name={payPal ? 'payment' : 'check_circle'} size={20} />
                                    <Typography variant="body2" sx={{ color: 'var(--oa-text-2)' }}>
                                        {translate(`marketplaceBuyer.checkout.settlement.${settlement}`)}
                                    </Typography>
                                </Box>
                            </Box>

                            {payPal && configError && (
                                <Alert severity="warning">{translate('marketplaceBuyer.checkout.paypalUnavailable')}</Alert>
                            )}
                            {payPal && config && !sdkUsable && (
                                <Alert severity="warning">{translate('marketplaceBuyer.checkout.sellerNoPaypal')}</Alert>
                            )}
                            {listing.alreadyLicensed && (
                                <Alert severity="info">{translate(listing.isBundle ? 'marketplaceBuyer.bundle.alreadyLicensed' : 'marketplaceBuyer.checkout.alreadyLicensed')}</Alert>
                            )}
                        </>
                    )}

                    {step === 'pay' && order && (
                        <Box data-testid="pay-step" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Alert severity="info">
                                {translate('marketplaceBuyer.checkout.orderCreated', {
                                    number: order.orderNumber ?? '',
                                    total: formatEuro(order.total, order.currency),
                                })}
                            </Alert>
                            {Number(order.taxTotal) > 0 && (
                                <Typography variant="body2" sx={{ color: 'var(--oa-text-2)' }}>
                                    {translate('marketplaceBuyer.checkout.taxBreakdown', {
                                        net: formatEuro(order.netTotal, order.currency),
                                        tax: formatEuro(order.taxTotal, order.currency),
                                        rate: order.taxRate,
                                    })}
                                </Typography>
                            )}
                            {sdk.error && <Alert severity="error">{translate('marketplaceBuyer.checkout.paypalUnavailable')}</Alert>}
                            {!sdk.ready && !sdk.error && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={28} />
                                </Box>
                            )}
                            {sdk.ready && sdk.paypal && order.paypalOrderId && (
                                <PayPalButtons
                                    paypal={sdk.paypal}
                                    paypalOrderId={order.paypalOrderId}
                                    onApprove={capture}
                                    onCancel={() => setError(translate('marketplaceBuyer.checkout.cancelled'))}
                                    onError={() => setError(translate('marketplaceBuyer.checkout.paypalError'))}
                                />
                            )}
                        </Box>
                    )}

                    {step === 'done' && (
                        <Alert severity="success" data-testid="done-step">
                            {translate(settlement === 'paypal' ? 'marketplaceBuyer.checkout.doneBody.paypal' : `marketplaceBuyer.checkout.doneBody.${settlement}`)}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                {step === 'done' ? (
                    <>
                        <Button onClick={onClose}>{translate('marketplaceBuyer.checkout.close')}</Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                onClose();
                                navigate('/marketplace/licenses');
                            }}
                        >
                            {translate('marketplaceBuyer.checkout.toLicenses')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={onClose} disabled={busy}>{translate('marketplaceBuyer.checkout.cancel')}</Button>
                        {step === 'configure' && (
                            <Button
                                variant="contained"
                                onClick={startCheckout}
                                disabled={busy || listing.alreadyLicensed || allowed.length === 0 || (payPal && !!config && !sdkUsable) || (payPal && configError)}
                                startIcon={busy ? <CircularProgress size={18} /> : undefined}
                            >
                                {buttonLabel}
                            </Button>
                        )}
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CheckoutDialog;
